import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, DataTable, EmptyState, Segmented, Sheet, SimpleTable, Spinner, Tag, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkDataColumn } from "@NP-Webber/crm-kit/dist/next/index.js";
import { yemotOutboundApi, type YemotSendDetail, type YemotSendDto, type YemotSendKind, type YemotSendStatus } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { useShell } from "../shell/ShellContext";
import { errorMessage, fmtDateTime, fmtNum, type Tone } from "./salesUi";
import "./sales.css";

const KIND_LABEL: Record<YemotSendKind, string> = { tzintuk: "צינתוק", voice: "הודעה קולית", call: "שיחה יוצאת" };
const STATUS_META: Record<YemotSendStatus, { label: string; tone: Tone }> = {
  sending: { label: "בשיגור", tone: "info" },
  sent: { label: "נשלח", tone: "success" },
  failed: { label: "נכשל", tone: "danger" },
};
/** entry statuses Yemot reports per number */
const ENTRY_LABEL: Record<string, string> = {
  pending: "ממתין",
  active: "בחיוג",
  ringing: "מצלצל",
  redial: "חיוג חוזר",
  up: "נענה",
  done: "הסתיים",
  bridged: "חובר",
  accepted: "נענה",
  no_answer: "לא נענה",
  busy: "תפוס",
  failed: "נכשל",
  amd: "משיבון",
  canceled: "בוטל",
  cancelled: "בוטל",
  error: "שגיאה",
  blocked: "חסום",
};
const ENTRY_TONE = (s: string | null): Tone =>
  !s ? "neutral" : ["up", "done", "bridged", "accepted"].includes(s) ? "success" : ["no_answer", "busy", "failed", "amd", "canceled", "cancelled", "error", "blocked"].includes(s) ? "danger" : "info";

type Recipient = YemotSendDetail["recipients"][number];

/** one send: what went out, the recipients and their status — refreshed from Yemot on demand (like Turbo) */
function SendSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<YemotSendDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (refresh = false) => {
      if (refresh) setBusy(true);
      try {
        const d = await yemotOutboundApi.get(id, refresh);
        setDetail(d);
        setError(null);
        if (refresh) {
          if (d.refreshed?.error) toast.error(d.refreshed.error);
          else toast.success(d.refreshed?.synced ? `הסטטוס עודכן מימות (${fmtNum(d.refreshed.synced)} מספרים)` : "הסטטוס עודכן מימות");
          onChanged();
        }
      } catch (e) {
        if (refresh) toast.error(errorMessage(e, "רענון הסטטוס נכשל"));
        else setError(errorMessage(e, "השיגור לא נמצא"));
      } finally {
        setBusy(false);
      }
    },
    [id, toast, onChanged],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const st = detail?.lastStatus;
  const live = st?.liveCounts;
  const sum = detail?.statusSummary;
  return (
    <Sheet open onClose={onClose} width={640} ariaLabel="שיגור">
      <div className="sl-sheet">
        <div className="sl-sheet__head">
          <div className="sl-sheet__title">
            <b>{detail?.title ?? "שיגור"}</b>
            {detail ? (
              <span className="sl-row">
                <Tag tone="neutral">{KIND_LABEL[detail.kind]}</Tag>
                <Tag tone={STATUS_META[detail.status].tone}>{STATUS_META[detail.status].label}</Tag>
                <span className="sl-muted">{fmtDateTime(detail.createdAt)}</span>
              </span>
            ) : null}
          </div>
          <div className="sl-row">
            {detail && detail.kind !== "call" ? (
              <Button size="sm" icon="refresh" disabled={busy} onClick={() => void load(true)}>
                {busy ? "מרענן…" : "רענון סטטוס מימות"}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" icon="x" iconOnly aria-label="סגירה" onClick={onClose} />
          </div>
        </div>
        <div className="sl-sheet__body">
          {error ? <Banner tone="danger">{error}</Banner> : null}
          {!detail && !error ? (
            <div className="sl-busy">
              <Spinner size={24} />
            </div>
          ) : null}
          {detail ? (
            <>
              {detail.error ? <Banner tone="danger">{detail.error}</Banner> : null}
              {st?.error ? <Banner tone="danger">{st.error}</Banner> : st?.hint ? <Banner tone="info">{st.hint}</Banner> : null}
              <dl className="sl-kv">
                <div>
                  <dt>נמענים</dt>
                  <dd>{fmtNum(detail.recipientCount)}</dd>
                </div>
                <div>
                  <dt>מספר מציג</dt>
                  <dd className="sl-ltr">{detail.callerId || "—"}</dd>
                </div>
                <div>
                  <dt>נשלח על ידי</dt>
                  <dd>{detail.createdByName || "—"}</dd>
                </div>
                <div>
                  <dt>סטטוס בימות</dt>
                  <dd>{st?.campaignStatus || (detail.lastStatusAt ? "—" : "עוד לא רוענן")}</dd>
                </div>
                {detail.lastStatusAt ? (
                  <div>
                    <dt>רוענן</dt>
                    <dd>{fmtDateTime(detail.lastStatusAt)}</dd>
                  </div>
                ) : null}
                {detail.dial.from ? (
                  <div>
                    <dt>חויג מ־</dt>
                    <dd className="sl-ltr">{detail.dial.from}</dd>
                  </div>
                ) : null}
                {typeof detail.dial.callsCount === "number" ? (
                  <div>
                    <dt>שיחות שיצאו</dt>
                    <dd>{fmtNum(detail.dial.callsCount)}</dd>
                  </div>
                ) : null}
              </dl>
              {live ? (
                <div className="sl-row">
                  <Tag tone="info">ממתינים {fmtNum(live.pending)}</Tag>
                  <Tag tone="info">בחיוג {fmtNum(live.ringing)}</Tag>
                  <Tag tone="success">הסתיימו {fmtNum(live.done)}</Tag>
                  {live.bridged ? <Tag tone="success">חוברו {fmtNum(live.bridged)}</Tag> : null}
                  {live.redial ? <Tag tone="warning">חיוג חוזר {fmtNum(live.redial)}</Tag> : null}
                  <Tag tone="danger">נכשלו {fmtNum(live.failed)}</Tag>
                  {live.blocked ? <Tag tone="danger">חסומים {fmtNum(live.blocked)}</Tag> : null}
                </div>
              ) : sum && sum.unknown < detail.recipients.length ? (
                <div className="sl-row">
                  <Tag tone="success">נענו {fmtNum(sum.answered)}</Tag>
                  <Tag tone="danger">לא נענו {fmtNum(sum.unanswered)}</Tag>
                  {sum.unknown ? <Tag tone="neutral">בלי סטטוס {fmtNum(sum.unknown)}</Tag> : null}
                </div>
              ) : null}
              {detail.messageText ? (
                <>
                  <h4 className="sl-section-title">הטקסט שהוקרא</h4>
                  <p className="sl-note" style={{ whiteSpace: "pre-wrap" }}>
                    {detail.messageText}
                  </p>
                </>
              ) : null}
              {detail.skipped && (detail.skipped.noPhone || detail.skipped.duplicate || detail.skipped.abroad) ? (
                <p className="sl-muted">
                  לא שוגרו:{" "}
                  {[
                    detail.skipped.noPhone ? `${fmtNum(detail.skipped.noPhone)} בלי טלפון` : null,
                    detail.skipped.duplicate ? `${fmtNum(detail.skipped.duplicate)} טלפון כפול` : null,
                    detail.skipped.abroad ? `${fmtNum(detail.skipped.abroad)} מספר חו״ל` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}
              <h4 className="sl-section-title">נמענים ({fmtNum(detail.recipients.length)})</h4>
              <SimpleTable
                ariaLabel="נמענים"
                rows={detail.recipients}
                rowKey="id"
                maxHeight={420}
                onRowClick={(r: Recipient) => r.leadId && navigate(`/sales/leads/${r.leadId}`)}
                empty="אין נמענים"
                columns={[
                  { key: "name", label: "שם", render: (r: Recipient) => r.name || "—" },
                  { key: "phone", label: "טלפון", ltr: true },
                  {
                    key: "yemotStatus",
                    label: "סטטוס",
                    render: (r: Recipient) =>
                      r.dialError ? (
                        <span className="sl-cell2">
                          <Tag tone="danger">לא חויג</Tag>
                          <small>{r.dialError}</small>
                        </span>
                      ) : r.yemotStatus ? (
                        <Tag tone={ENTRY_TONE(r.yemotStatus)}>{ENTRY_LABEL[r.yemotStatus] ?? r.yemotStatus}</Tag>
                      ) : (
                        <span className="sl-muted">—</span>
                      ),
                  },
                ]}
              />
            </>
          ) : null}
        </div>
      </div>
    </Sheet>
  );
}

/**
 * /sales/sends — «שיגורים בקו» (Turbo's היסטוריית שיגורים): every tzintuk, voice message and click-to-call from the
 * Yemot line, with the recipients. Statuses are refreshed from Yemot on demand — no background polling, like Turbo.
 */
export function YemotSendsPage() {
  const navigate = useNavigate();
  const { allowed } = useShell();
  const [kind, setKind] = useState<YemotSendKind | "">("");
  const [rows, setRows] = useState<YemotSendDto[] | null>(null);
  const [byKind, setByKind] = useState<Partial<Record<YemotSendKind, number>>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    yemotOutboundApi
      .list({ kind, limit: 200 })
      .then((r) => {
        setRows(r.items);
        setByKind(r.byKind);
        setError(null);
      })
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את השיגורים")))
      .finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => load(), [load]);

  const columns = useMemo<Array<CkDataColumn<YemotSendDto>>>(
    () => [
      { key: "createdAt", label: "תאריך", type: "date", sortable: true, render: (r) => fmtDateTime(r.createdAt) },
      { key: "title", label: "כותרת", type: "text", sortable: true, render: (r) => <b>{r.title}</b> },
      { key: "kind", label: "סוג", type: "enum", sortable: true, getValue: (r) => KIND_LABEL[r.kind] },
      {
        key: "status",
        label: "סטטוס",
        type: "enum",
        sortable: true,
        getValue: (r) => STATUS_META[r.status].label,
        render: (r) => <Tag tone={STATUS_META[r.status].tone}>{STATUS_META[r.status].label}</Tag>,
      },
      { key: "recipientCount", label: "נמענים", type: "number", sortable: true, sum: true },
      { key: "campaignStatus", label: "סטטוס בימות", type: "text", getValue: (r) => r.lastStatus?.campaignStatus ?? "" },
      { key: "createdByName", label: "נשלח על ידי", type: "enum", sortable: true, getValue: (r) => r.createdByName ?? "" },
      { key: "error", label: "שגיאה", type: "text", getValue: (r) => r.error ?? "" },
    ],
    [],
  );

  const total = Object.values(byKind).reduce((a, b) => a + (b ?? 0), 0);
  return (
    <div className="sl-page">
      <ScreenHeader
        sub="היסטוריית השיגורים מקו ימות המשיח: צינתוקים, הודעות קוליות ושיחות יוצאות ללידים. רענון סטטוס לפי לחיצה — בלי בדיקה אוטומטית ברקע."
        primary={allowed("sales.view") ? { label: "לרשימת הלידים", icon: "phone", onClick: () => navigate("/sales/leads") } : undefined}
      />
      <div className="sl-tabs">
        <Segmented
          ariaLabel="סוג"
          value={kind}
          onChange={(v) => setKind(v as YemotSendKind | "")}
          items={[
            { value: "", label: "הכל", count: rows ? total : undefined },
            { value: "tzintuk", label: "צינתוקים", count: rows ? byKind.tzintuk ?? 0 : undefined },
            { value: "voice", label: "הודעות קוליות", count: rows ? byKind.voice ?? 0 : undefined },
            { value: "call", label: "שיחות יוצאות", count: rows ? byKind.call ?? 0 : undefined },
          ]}
        />
      </div>
      {error && rows ? <Banner tone="danger">{error}</Banner> : null}
      {rows && !rows.length && !kind ? (
        <EmptyState icon="bell" title="עדיין אין שיגורים" action={<Button onClick={() => navigate("/sales/leads")}>לרשימת הלידים</Button>}>
          בחרו לידים ברשימה ← «צינתוק» או «הודעה קולית», או «חיוג» מכרטיס הליד.
        </EmptyState>
      ) : (
        <DataTable<YemotSendDto>
          columns={columns}
          rows={rows ?? []}
          rowKey="id"
          loading={loading && !rows}
          error={rows ? null : error}
          onRetry={load}
          onRefresh={load}
          storageKey="tb.sales.yemotSends"
          selectable={false}
          searchPlaceholder="חיפוש כותרת, שולח…"
          activeRowId={openId}
          onRowClick={(r) => setOpenId(r.id)}
          rowActions={(r) => [{ key: "open", icon: "expand", label: "פירוט", onClick: () => setOpenId(r.id) }]}
          exportFilename="שיגורים-בקו"
          emptyTitle="אין שיגורים בסינון"
          emptyText="נסו לשונית אחרת."
          renderMobileRow={(r) => (
            <>
              <b>{r.title}</b>
              <small>{[KIND_LABEL[r.kind], STATUS_META[r.status].label, `${fmtNum(r.recipientCount)} נמענים`, fmtDateTime(r.createdAt)].join(" · ")}</small>
            </>
          )}
        />
      )}
      {openId ? <SendSheet key={openId} id={openId} onClose={() => setOpenId(null)} onChanged={load} /> : null}
    </div>
  );
}

export default YemotSendsPage;
